/**
 * Fixed-timestep game loop.
 *
 * ## Why fixed
 *
 * Every movement constant in `engine/physics.ts` is expressed in pixels per
 * 1/60s frame, because that is how the original games document theirs. A
 * variable timestep would make Tavi's top speed depend on the display's
 * refresh rate, and would make the physics non-reproducible: the same inputs
 * would produce different results on a 60Hz and a 144Hz screen.
 *
 * So the simulation only ever advances in exact `DT` steps. Real elapsed time is
 * accumulated and drained in whole steps, and any leftover fraction is carried
 * into the next frame.
 *
 * ## Frame skipping
 *
 * When a frame is late, the loop runs *several* simulation steps to catch up.
 * That is what makes physics time-consistent across a skip: a 100ms hitch is
 * six 1/60s steps, not one 100ms step, so Tavi travels the same distance he
 * would have in six normal frames.
 *
 * Two things bound the catch-up, and both are deliberate:
 *
 * - A single frame's elapsed time is clamped. A backgrounded tab can produce a
 *   multi-second delta, and simulating all of it would freeze the page.
 * - Accumulated debt is capped, and time beyond the cap is *dropped and
 *   counted* rather than silently discarded. Without the cap, a machine that
 *   cannot sustain the step count would fall permanently behind and never
 *   recover — the classic spiral of death, where each catch-up frame makes the
 *   next one later. `droppedSeconds` makes the loss visible instead of
 *   pretending the simulation is keeping up.
 *
 * ## Animation
 *
 * Anything animated from `level.tick` or a step counter is automatically
 * frame-skip safe, because ticks advance in exact 1/60s increments regardless
 * of how many real frames elapsed. Anything animated from wall-clock time is
 * not, and will visibly jump. Animation phases should therefore be driven from
 * the tick or the equivalent step counter rather than from `performance.now()`.
 */

import { DT, MAX_FRAME_TIME } from '../engine/constants'

/** Most simulation steps to run for one real frame. */
export const MAX_STEPS_PER_FRAME = 8

export interface LoopCallbacks {
  /** Fixed 1/60s simulation step. */
  update(dt: number): void
  /** Draw. */
  render(): void
}

/**
 * The pure timing half of the loop, with no DOM or timer dependencies so it can
 * be tested directly.
 *
 * Feed it real elapsed seconds; it tells you how many fixed steps to run.
 */
export class FixedStepClock {
  private accumulator = 0
  private readonly stepSeconds: number
  private readonly maxSteps: number
  private readonly maxFrameSeconds: number

  private simulated = 0
  private dropped = 0
  private clamped = 0
  private measured = 0

  constructor(
    stepSeconds: number = DT,
    maxSteps: number = MAX_STEPS_PER_FRAME,
    maxFrameSeconds: number = MAX_FRAME_TIME,
  ) {
    this.stepSeconds = stepSeconds
    this.maxSteps = Math.max(1, maxSteps)
    this.maxFrameSeconds = maxFrameSeconds
  }

  /**
   * Account for `elapsedSeconds` of real time and return how many fixed steps
   * to simulate now.
   *
   * Non-finite and negative inputs are treated as zero rather than propagated:
   * a single bad timestamp from the browser should not poison the accumulator
   * and take the simulation with it.
   */
  advance(elapsedSeconds: number): number {
    let elapsed = elapsedSeconds
    if (!Number.isFinite(elapsed) || elapsed < 0) elapsed = 0
    // A backgrounded tab or a long GC pause can hand us seconds at once. This
    // is a deliberate policy, not a failure, so it is counted separately from
    // `dropped`: we never *tried* to simulate it.
    if (elapsed > this.maxFrameSeconds) {
      this.clamped += elapsed - this.maxFrameSeconds
      elapsed = this.maxFrameSeconds
    }

    this.measured += elapsed
    this.accumulator += elapsed

    // Plain floor, deliberately. The usual fixed-timestep folklore says to add a
    // nanosecond of epsilon here so that float error cannot tip the division
    // into an extra step and leave the game running permanently fast. Measured
    // over ten minutes at 1/60s -- and at 1, 10 and 100 parts-per-million fast,
    // and with +/-30% frame jitter -- the guard changes nothing, because the
    // accumulator is bounded and the arithmetic is well conditioned for this
    // step size. So it is not here: an unexplained fudge factor is worse than
    // no fudge factor, because the next person cannot tell what it is for.
    // `tests/loop.test.ts` pins the no-drift property instead, which is the
    // thing that actually matters.
    let steps = Math.floor(this.accumulator / this.stepSeconds)
    if (steps > this.maxSteps) {
      // Too far behind to catch up this frame. Drop the excess rather than
      // carrying it, or the next frame is even later and we never recover.
      const excessSteps = steps - this.maxSteps
      this.accumulator -= excessSteps * this.stepSeconds
      this.dropped += excessSteps * this.stepSeconds
      steps = this.maxSteps
    }

    this.accumulator -= steps * this.stepSeconds
    if (this.accumulator < 0) this.accumulator = 0
    this.simulated += steps * this.stepSeconds
    return steps
  }

  /**
   * Seconds of real time discarded because the machine could not keep up.
   * Should stay at zero on any machine that can sustain 60 steps a second.
   *
   * Distinct from `clampedSeconds`: this is time we *tried* to simulate and
   * could not, which is a performance problem rather than a policy.
   */
  get droppedSeconds(): number {
    return this.dropped
  }

  /**
   * Seconds deliberately not simulated because a single frame exceeded the
   * clamp. A backgrounded tab produces these; they are expected, not a fault.
   */
  get clampedSeconds(): number {
    return this.clamped
  }

  /**
   * Simulated minus measured, in seconds. Zero means the simulation is exactly
   * in step with the wall clock; positive means we are running behind.
   */
  get driftSeconds(): number {
    return this.simulated - (this.measured - this.dropped)
  }

  /** The caps in force, for the debug overlay. */
  get limits(): { step: number; maxSteps: number; maxFrame: number } {
    return {
      step: this.stepSeconds,
      maxSteps: this.maxSteps,
      maxFrame: this.maxFrameSeconds,
    }
  }

  /** Held-over time not yet enough for a whole step. */
  get pendingSeconds(): number {
    return this.accumulator
  }

  reset(): void {
    this.accumulator = 0
    this.simulated = 0
    this.dropped = 0
    this.clamped = 0
    this.measured = 0
  }
}

/**
 * Drives `FixedStepClock` from `requestAnimationFrame`.
 *
 * rAF is the right primitive here: the browser paces it to the display's
 * refresh and, critically, pauses it entirely when the tab is hidden — so we
 * never spin simulating a game nobody is looking at.
 */
export class GameLoop {
  private readonly clock: FixedStepClock
  private lastTime = 0
  private running = false
  private readonly callbacks: LoopCallbacks

  /** Rolling average frames per second, over a half-second window. */
  fps = 60
  /** Rolling average simulation steps per real frame. 1.0 is a clean 60fps. */
  stepsPerFrame = 1
  private fpsElapsed = 0
  private fpsFrames = 0
  private stepSamples = 0

  constructor(
    callbacks: LoopCallbacks,
    clock: FixedStepClock = new FixedStepClock(),
  ) {
    this.callbacks = callbacks
    this.clock = clock
  }

  /** Timing telemetry, for the debug overlay. */
  get timing(): {
    fps: number
    stepsPerFrame: number
    drift: number
    dropped: number
    clamped: number
    pending: number
    limits: { step: number; maxSteps: number; maxFrame: number }
  } {
    return {
      fps: this.fps,
      stepsPerFrame: this.stepsPerFrame,
      drift: this.clock.driftSeconds,
      dropped: this.clock.droppedSeconds,
      clamped: this.clock.clampedSeconds,
      pending: this.clock.pendingSeconds,
      limits: this.clock.limits,
    }
  }

  start(): void {
    if (this.running) return
    this.running = true
    this.clock.reset()
    this.lastTime = performance.now()
    requestAnimationFrame(this.tick)
  }

  private tick = (now: number): void => {
    if (!this.running) return
    // Queue the next frame first, so a throwing callback cannot stop the loop.
    requestAnimationFrame(this.tick)

    const elapsed = (now - this.lastTime) / 1000
    this.lastTime = now

    const steps = this.clock.advance(elapsed)

    this.fpsElapsed += Math.max(0, elapsed)
    this.fpsFrames++
    this.stepSamples += steps
    if (this.fpsElapsed >= 0.5) {
      this.fps = this.fpsFrames / this.fpsElapsed
      this.stepsPerFrame = this.stepSamples / this.fpsFrames
      this.fpsElapsed = 0
      this.fpsFrames = 0
      this.stepSamples = 0
    }

    for (let i = 0; i < steps; i++) this.callbacks.update(DT)

    this.callbacks.render()
  }
}
